import { uniqueId } from "lodash-es";
import uuid4 from "uuid4";

export class IdHelper {
  private static readonly tempFeIdPrefix = "temp-fe-id-";

  /** Generates a unique ID. */
  public static newId(): string {
    return this.newUuid4();
  }

  /** Generates a unique UUID4. */
  public static newUuid4(): string {
    return uuid4();
  }

  public static isTempFeId(id: Nil<string>): boolean {
    return !!id && id.startsWith(this.tempFeIdPrefix);
  }

  /** Generates a unique ID for temporal use on FE. */
  public static newTempFeId(): string {
    return uniqueId(this.tempFeIdPrefix);
  }

  public static nullIfTempFeId(id: Nil<string>): Nil<string> {
    return !id || this.isTempFeId(id) ? null : id;
  }

  public static excludeTempFeIds(ids: string[]): string[] {
    return !ids ? ids : ids.filter((id) => !this.isTempFeId(id));
  }

  /** Deep traverses the object/array and sets all FE temp ids to null.
   * For array, it removes the elements with temp ids.
   */
  public static setAllTempFeIdsToNullDeep<T extends object | object[]>(obj: T): T {
    function traverse(value: any, isTempFeId: (id: string) => boolean): any {
      if (!value) {
        return value;
      }
      if (typeof value === "string" && isTempFeId(value)) {
        return null;
      }
      if (Array.isArray(value)) {
        return value.map((x) => traverse(x, isTempFeId)).filter((x) => x !== null);
      }
      if (value && typeof value === "object") {
        const result: any = {};
        for (const key in value) {
          if (typeof value[key] === "string" && isTempFeId(value[key])) {
            result[key] = null;
          } else {
            result[key] = traverse(value[key], isTempFeId);
          }
        }
        return result;
      }
      return value;
    }

    return traverse(obj, this.isTempFeId.bind(this));
  }
}
